/** A selectable scan game, shared by the capture picker (ScanCamera) and the manual-search picker
 *  (ScanSearchSheet) so both match the backend game vocabulary. `value` is the canonical GAME_KEY
 *  the backend expects.
 *
 *  A game belongs in `GAMES` only once its card images are recognizable; otherwise the picker
 *  offers an option that can never match (#162). Pokémon (Japan) is its own entry because the
 *  selection scopes recognition and browsing to the Japanese corpus and catalog, which share
 *  Scrydex's Pokémon id space with English cards.
 *
 *  `label` is the full name that assistive tech announces and wider surfaces (the desktop identify
 *  screen) show. `short` is what a chip shows, so long formal names do not push other games off a
 *  narrow row; games without one fall back to `label`. */
export type Game = { value: string; label: string; short?: string; };

export const GAMES: readonly Game[] = [
  { value: "pokemon", label: "Pokémon" },
  { value: "pokemon-jp", label: "Pokémon (Japan)", short: "Pokémon JP" },
  { value: "lorcana", label: "Lorcana" },
  { value: "riftbound", label: "Riftbound" },
  { value: "onepiece", label: "One Piece" },
  { value: "magic", label: "Magic: The Gathering", short: "Magic" },
  {
    value: "dragonball-fusion-world",
    label: "Dragon Ball Super: Fusion World",
    short: "DBS Fusion World",
  },
  { value: "dragonball-masters", label: "Dragon Ball Super: Masters", short: "DBS Masters" },
];

/** The game a picker starts on before anything is restored: the first entry in `GAMES`, so it
 *  follows a reorder. It lives beside the list so a surface that only needs the constant does not
 *  import the persistence hook and its storage dependency. */
export const DEFAULT_SCAN_GAME = GAMES[0].value;

/** Returns the chip text for a game: the short form when it has one, else the full name. */
export const chipLabel = (game: Game): string => game.short ?? game.label;

/** Returns the full name for a GAME_KEY, for prose such as "No matches in One Piece", so it never
 *  abbreviates. Falls back to the key itself so an unknown key still reads as something. */

export const gameLabel = (value: string): string =>
  GAMES.find((g) => g.value === value)?.label ?? value;
